import React, { useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LibraryAddOutlinedIcon from '@mui/icons-material/LibraryAddOutlined';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ReorderOutlinedIcon from '@mui/icons-material/ReorderOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import theme from '../../../styles/theme';
import { useStyles } from './Agreements.styles';
import AppButton from '../../../components/designe/app-button/AppButton';
import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import AppSelectWrapper from '../../../components/designe/app-select-wrapper/AppSelectWrapper';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import AppTabs from '../../../components/designe/app-tabs/AppTabs';
import { Paths } from '../../../shared/paths';
import {
  useAgreementsComponent,
  AGREEMENT_TYPES,
} from './useAgreementsComponent';
import {
  AGREEMENT_TYPE_PATH_SEGMENT,
  agreementTypeFromPathSegment,
} from './agreementsTypePaths';
import {
  AgreementDocumentType,
  IAgreementItem,
} from '../../../types/interfaces/agreement';

const DND_ITEM_TYPE = 'agreement-item';
const DndProviderCompat = DndProvider as React.ComponentType<any>;

function getItemPreview(value: string) {
  const text = value || '';
  if (text.length <= 50) return text;
  return `${text.slice(0, 50)}...`;
}

function SortableItem({
  index,
  item,
  classes,
  moveItem,
  selectedLanguageKey,
}: {
  index: number;
  item: IAgreementItem;
  classes: Record<string, string>;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  selectedLanguageKey: string;
}) {
  const preview =
    item.translations?.find(
      (translation) => translation.languageKey === selectedLanguageKey,
    )?.value ?? '';
  const [{ isDragging }, drag] = useDrag<
    { type: string; index: number },
    unknown,
    { isDragging: boolean }
  >({
    item: { type: DND_ITEM_TYPE, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const [{ isOver }, drop] = useDrop<
    { type: string; index: number },
    unknown,
    { isOver: boolean }
  >({
    accept: DND_ITEM_TYPE,
    drop: (dragged: { type: string; index: number }) => {
      if (dragged.index !== index) moveItem(dragged.index, index);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`${classes.dragItem} ${isOver ? classes.dragOver : ''}`}
      style={{ opacity: isDragging ? 0.5 : 1 }}>
      <span className={classes.dragTypeLabel}>{item.type}</span>
      <Typography variant="text-md" className={classes.dragPreviewText}>
        {getItemPreview(preview)}
      </Typography>
    </div>
  );
}

function AgreementItemForm({
  item,
  languages,
  itemTypeOptions,
  onSubmit,
  onCancel,
  isLoading,
}: {
  item?: IAgreementItem | null;
  languages: { key: string; title: string }[];
  itemTypeOptions: { id: string; name: string }[];
  onSubmit: (payload: {
    itemId?: string;
    type: string;
    translations: { languageKey: string; value: string }[];
  }) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  const [type, setType] = useState(item?.type || itemTypeOptions[0]?.id || '');
  const [translations, setTranslations] = useState(
    languages.map((language) => ({
      languageKey: language.key,
      value:
        item?.translations?.find((x) => x.languageKey === language.key)
          ?.value ?? '',
    })),
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1.5}
      width="100%"
      minWidth={0}>
      <AppSelectWrapper
        name="type"
        label={t('common.type')}
        options={itemTypeOptions}
        defaultValue={itemTypeOptions.find((option) => option.id === type)}
        onChange={(event) => setType(event?.target?.value?.id || '')}
      />
      {languages.map((language, languageIndex) => (
        <Box key={language.key}>
          <Typography variant="text-sm-sb">{language.title}</Typography>
          <textarea
            value={translations[languageIndex]?.value || ''}
            onChange={(event) =>
              setTranslations((current) =>
                current.map((translation, currentIndex) =>
                  currentIndex === languageIndex
                    ? { ...translation, value: event.target.value }
                    : translation,
                ),
              )
            }
            style={{
              width: '100%',
              minHeight: 120,
              borderColor: '#d7d7d7',
              borderRadius: 6,
              padding: 10,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
        </Box>
      ))}
      <Box display="flex" gap={1} justifyContent="flex-end">
        <AppButton variant="outlined" onClick={onCancel} disabled={isLoading}>
          {t('common.cancel')}
        </AppButton>
        <AppButton
          startIcon={item?.id ? <SaveOutlinedIcon /> : <AddOutlinedIcon />}
          onClick={() =>
            onSubmit({
              itemId: item?.id,
              type,
              translations,
            })
          }
          disabled={isLoading || !type}>
          {t('common.save')}
        </AppButton>
      </Box>
    </Box>
  );
}

function AgreementsPageBody({
  documentType,
}: {
  documentType: AgreementDocumentType;
}) {
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const {
    state,
    dispatch,
    isFetching,
    versions,
    selectedVersion,
    languages,
    blocksForSelectedLanguage,
    itemTypeOptions,
    sourceVersionId,
    setSourceVersionId,
    canPublish,
    canArchive,
    canCreateVersion,
    canCreateItem,
    canEditItem,
    canDeleteItem,
    canReorderItems,
    draftOrderItems,
    setDraftOrderItems,
    onCreateVersion,
    onPublishVersion,
    onArchiveVersion,
    onDeleteItem,
    onSaveItem,
    onCreateItem,
    onOpenSortModal,
    onSaveSort,
    isBusy,
  } = useAgreementsComponent(documentType);

  const versionOptions = useMemo(
    () =>
      versions.map((version) => ({
        id: version.id,
        name: `${version.version} (${version.status})`,
      })),
    [versions],
  );

  const languageOptions = useMemo(
    () =>
      languages.map((language) => ({ id: language.key, name: language.title })),
    [languages],
  );

  const sourceVersionOptions = useMemo(
    () =>
      versions.map((version) => ({
        id: String(version.version),
        name: `${version.version} (${version.status})`,
      })),
    [versions],
  );

  const typeLabels = useMemo(
    () => ({
      termsOfUse: t('adminDashboard.agreementsSection.type.termsOfUse'),
      privacyPolicy: t('adminDashboard.agreementsSection.type.privacyPolicy'),
      cookiePolicy: t('adminDashboard.agreementsSection.type.cookiePolicy'),
    }),
    [t],
  );

  function onMoveItem(dragIndex: number, hoverIndex: number) {
    setDraftOrderItems((current) => {
      const updated = [...current];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(hoverIndex, 0, moved);
      return updated;
    });
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.topPanel}>
        <div className={classes.selectorsRow}>
          <div className={classes.title}>
            <Typography variant="text-lg-sb">
              {typeLabels[documentType]}
            </Typography>
          </div>
          <div className={classes.selectors}>
            <AppSelectWrapper
              key={`version-${documentType}`}
              className={classes.selector}
              name="version"
              label={t('adminDashboard.agreementsSection.version')}
              options={versionOptions}
              defaultValue={versionOptions.find(
                (option) => option.id === state.selectedVersionId,
              )}
              onChange={(event) =>
                dispatch({
                  type: 'SET_SELECTED_VERSION',
                  payload: event?.target?.value?.id ?? null,
                })
              }
            />
            <AppSelectWrapper
              className={classes.selector}
              name="language"
              label={t('adminDashboard.agreementsSection.language')}
              options={languageOptions}
              defaultValue={languageOptions.find(
                (option) => option.id === state.selectedLanguageKey,
              )}
              onChange={(event) =>
                dispatch({
                  type: 'SET_SELECTED_LANGUAGE',
                  payload: event?.target?.value?.id ?? null,
                })
              }
            />
          </div>
        </div>
        <div className={classes.actionsRow}>
          <AppButton
            variant="outlined"
            startIcon={<LibraryAddOutlinedIcon />}
            onClick={() => dispatch({ type: 'OPEN_CREATE_VERSION_MODAL' })}
            disabled={!selectedVersion || isBusy || !canCreateVersion}>
            {t('adminDashboard.agreementsSection.addVersion')}
          </AppButton>
          <AppButton
            variant="outlined"
            startIcon={<PostAddOutlinedIcon />}
            onClick={() => dispatch({ type: 'OPEN_CREATE_ITEM_MODAL' })}
            disabled={!selectedVersion || isBusy || !canCreateItem}>
            {t('adminDashboard.agreementsSection.addBlock', 'Add Block')}
          </AppButton>
          <AppButton
            variant="outlined"
            startIcon={<PublishOutlinedIcon />}
            onClick={() => dispatch({ type: 'OPEN_PUBLISH_CONFIRM' })}
            disabled={!selectedVersion || !canPublish || isBusy}>
            {t('adminDashboard.agreementsSection.publish')}
          </AppButton>
          <AppButton
            variant="outlined"
            startIcon={<ArchiveOutlinedIcon />}
            onClick={() => dispatch({ type: 'OPEN_ARCHIVE_CONFIRM' })}
            disabled={!selectedVersion || !canArchive || isBusy}>
            {t('adminDashboard.agreementsSection.archive')}
          </AppButton>
          <AppButton
            variant="outlined"
            startIcon={<ReorderOutlinedIcon />}
            onClick={onOpenSortModal}
            disabled={
              !selectedVersion ||
              !blocksForSelectedLanguage.length ||
              isBusy ||
              !canReorderItems
            }>
            {t('adminDashboard.agreementsSection.sort')}
          </AppButton>
        </div>
      </div>

      <div className={classes.blocksList}>
        {!blocksForSelectedLanguage.length && (
          <Typography className={classes.emptyState} variant="text-md">
            {isFetching
              ? t('adminDashboard.agreementsSection.loading')
              : t('adminDashboard.agreementsSection.noBlocksFound')}
          </Typography>
        )}
        {blocksForSelectedLanguage.map(
          (item: IAgreementItem & { preview?: string }) => (
            <div key={item.id} className={classes.blockRow}>
              <div className={classes.blockContent}>
                <Typography variant="text-md">
                  <strong>
                    {t('adminDashboard.agreementsSection.typeLabel')}:
                  </strong>{' '}
                  {item.type}
                </Typography>
                <Typography variant="text-md" className={classes.blockValue}>
                  {item.preview || '-'}
                </Typography>
              </div>
              <div className={classes.blockActions}>
                <IconButton
                  disabled={!canEditItem || isBusy}
                  onClick={() =>
                    dispatch({ type: 'OPEN_EDIT_ITEM_MODAL', payload: item })
                  }>
                  <EditIcon />
                </IconButton>
                <IconButton
                  disabled={!canDeleteItem || isBusy}
                  onClick={() =>
                    dispatch({ type: 'OPEN_DELETE_MODAL', payload: item })
                  }>
                  <DeleteIcon />
                </IconButton>
              </div>
            </div>
          ),
        )}
      </div>
      <div className={classes.bottomActions}>
        <AppButton
          variant="outlined"
          startIcon={<PostAddOutlinedIcon />}
          onClick={() => dispatch({ type: 'OPEN_CREATE_ITEM_MODAL' })}
          disabled={!selectedVersion || isBusy || !canCreateItem}>
          {t('adminDashboard.agreementsSection.addBlock', 'Add Block')}
        </AppButton>
      </div>

      <AppDialog
        open={state.isCreateVersionModalOpen}
        onClose={() => dispatch({ type: 'CLOSE_CREATE_VERSION_MODAL' })}
        title={t('adminDashboard.agreementsSection.addVersion')}>
        <Box minWidth={420}>
          <AppSelectWrapper
            name="sourceVersion"
            label={t('adminDashboard.agreementsSection.sourceVersion')}
            options={sourceVersionOptions}
            defaultValue={sourceVersionOptions.find(
              (option) => option.id === sourceVersionId,
            )}
            onChange={(event) =>
              setSourceVersionId(event?.target?.value?.id || '')
            }
          />
          <Box display="flex" justifyContent="flex-end" marginTop={2} gap={1}>
            <AppButton
              variant="outlined"
              disabled={isBusy}
              onClick={() => dispatch({ type: 'CLOSE_CREATE_VERSION_MODAL' })}>
              {t('common.cancel')}
            </AppButton>
            <AppButton
              startIcon={<AddOutlinedIcon />}
              onClick={onCreateVersion}
              disabled={isBusy}>
              {t('common.create')}
            </AppButton>
          </Box>
        </Box>
      </AppDialog>

      <AppDialog
        open={state.isCreateItemModalOpen}
        onClose={() => dispatch({ type: 'CLOSE_CREATE_ITEM_MODAL' })}
        title={t('adminDashboard.agreementsSection.addBlock', 'Add Block')}>
        <AgreementItemForm
          languages={languages.map((language) => ({
            key: language.key,
            title: language.title,
          }))}
          itemTypeOptions={itemTypeOptions}
          onSubmit={(payload) =>
            onCreateItem({
              type: payload.type,
              translations: payload.translations,
            })
          }
          onCancel={() => dispatch({ type: 'CLOSE_CREATE_ITEM_MODAL' })}
          isLoading={isBusy}
        />
      </AppDialog>

      <AppDialog
        open={state.isEditItemModalOpen && !!state.itemForEdit}
        onClose={() => dispatch({ type: 'CLOSE_EDIT_ITEM_MODAL' })}
        title={t('adminDashboard.agreementsSection.editBlock')}>
        <AgreementItemForm
          item={state.itemForEdit}
          languages={languages.map((language) => ({
            key: language.key,
            title: language.title,
          }))}
          itemTypeOptions={itemTypeOptions}
          onSubmit={(payload) => {
            if (!payload.itemId) return;
            onSaveItem({
              itemId: payload.itemId,
              type: payload.type,
              translations: payload.translations,
            });
          }}
          onCancel={() => dispatch({ type: 'CLOSE_EDIT_ITEM_MODAL' })}
          isLoading={isBusy}
        />
      </AppDialog>

      <AppDialog
        open={state.isSortModalOpen}
        onClose={() => dispatch({ type: 'CLOSE_SORT_MODAL' })}
        title={t('adminDashboard.agreementsSection.sortBlocks')}>
        <DndProviderCompat backend={HTML5Backend}>
          <Box minWidth={520}>
            {draftOrderItems.map((item, index) => (
              <SortableItem
                key={item.id}
                item={item}
                index={index}
                classes={classes}
                moveItem={onMoveItem}
                selectedLanguageKey={state.selectedLanguageKey}
              />
            ))}
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <AppButton
                variant="outlined"
                disabled={isBusy}
                onClick={() => dispatch({ type: 'CLOSE_SORT_MODAL' })}>
                {t('common.cancel')}
              </AppButton>
              <AppButton
                startIcon={<SaveOutlinedIcon />}
                onClick={onSaveSort}
                disabled={isBusy}>
                {t('adminDashboard.agreementsSection.saveOrder')}
              </AppButton>
            </Box>
          </Box>
        </DndProviderCompat>
      </AppDialog>

      <ConfirmDialog
        open={state.isDeleteModalOpen}
        onClose={() => dispatch({ type: 'CLOSE_DELETE_MODAL' })}
        title={t('adminDashboard.agreementsSection.deleteBlockTitle')}
        onSubmit={onDeleteItem}>
        {t('adminDashboard.agreementsSection.deleteBlockMessage')}
      </ConfirmDialog>

      <ConfirmDialog
        open={state.isPublishConfirmOpen}
        onClose={() => dispatch({ type: 'CLOSE_PUBLISH_CONFIRM' })}
        title={t('adminDashboard.agreementsSection.publishVersionTitle')}
        onSubmit={onPublishVersion}>
        {t('adminDashboard.agreementsSection.publishVersionMessage')}
      </ConfirmDialog>

      <ConfirmDialog
        open={state.isArchiveConfirmOpen}
        onClose={() => dispatch({ type: 'CLOSE_ARCHIVE_CONFIRM' })}
        title={t('adminDashboard.agreementsSection.archiveVersionTitle')}
        onSubmit={onArchiveVersion}>
        {t('adminDashboard.agreementsSection.archiveVersionMessage')}
      </ConfirmDialog>
    </div>
  );
}

function AgreementsRouteBridge() {
  const { agreementType } = useParams();
  const documentType = agreementTypeFromPathSegment(agreementType);
  if (!documentType) {
    return (
      <Navigate
        to={`${Paths.adminDashboard.root}${Paths.adminDashboard.agreements}/${AGREEMENT_TYPE_PATH_SEGMENT.termsOfUse}`}
        replace
      />
    );
  }
  return <AgreementsPageBody documentType={documentType} />;
}

const Agreements = () => {
  const { t } = useTranslation();
  const base = `${Paths.adminDashboard.root}${Paths.adminDashboard.agreements}`;
  const typeLabels = useMemo(
    () => ({
      termsOfUse: t('adminDashboard.agreementsSection.type.termsOfUse'),
      privacyPolicy: t('adminDashboard.agreementsSection.type.privacyPolicy'),
      cookiePolicy: t('adminDashboard.agreementsSection.type.cookiePolicy'),
    }),
    [t],
  );
  const tabsConfig = useMemo(
    () =>
      AGREEMENT_TYPES.map((type) => ({
        label: typeLabels[type],
        path: `${base}/${AGREEMENT_TYPE_PATH_SEGMENT[type]}`,
      })),
    [base, typeLabels],
  );

  return (
    <>
      <AppTabs config={tabsConfig} />
      <Box sx={{ mt: 2 }}>
        <Routes>
          <Route path=":agreementType" element={<AgreementsRouteBridge />} />
          <Route
            index
            element={
              <Navigate to={AGREEMENT_TYPE_PATH_SEGMENT.termsOfUse} replace />
            }
          />
        </Routes>
      </Box>
    </>
  );
};

export default Agreements;
